import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { GetParsedCampaignParams, GetParsedCampaignResponse } from 'pages/campaigns/campaignForm/campaignForm.types';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import { CAMPAIGN_FORM_STEP_ONE_FIELD_NAME } from './campaignFormStepOne.constants';

interface CampaignFormStepOneContextProps {
  isFormValid: boolean;
  getStepOneCampaignFormValues: (params: GetParsedCampaignParams) => GetParsedCampaignResponse;
}

const initCampaignFormStepOneContextData: CampaignFormStepOneContextProps = {
  isFormValid: false,
  getStepOneCampaignFormValues: () => {
    return {};
  },
};

export const CampaignFormStepOneContext = createContext<CampaignFormStepOneContextProps>(
  initCampaignFormStepOneContextData,
);

export const CampaignFormStepOneContextProvider = ({ children }: { children: ReactNode }) => {
  const { brand, products, campaignFormMethods, getParsedCampaign } = useContext(CampaignFormContext);
  const { description, ...campaignFormValues } = useWatch({
    control: campaignFormMethods.control,
  });

  const isFormValid = useMemo(() => {
    const requiredFields = [
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.DELIVERY_COMPANY_NAME,
      ...Object.values(CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD).filter(
        (name) => (
          name !== CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.INSPECTION_SUBMIT_DUE_DT &&
          name !== CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.INSPECTION_FEEDBACK_DUE_DT
        ),
      ),
    ];

    if (
      // 브랜드 설명이 없는 경우
      (!brand.isWholesale && !description) ||
      // 제품이 없는 경우
      !products ||
      products.length <= 0 ||
      // 일정 관련 데이터가 없는 경우
      requiredFields.some((name) => !campaignFormValues?.[name])
    ) {
      return false;
    } else {
      return true;
    }
  }, [brand, products, description, campaignFormValues]);

  const getStepOneCampaignFormValues = useCallback(({ fieldNames }: GetParsedCampaignParams) => {
    const names = [
      'outputTypeFl',
      'description',
      'isFasttrackSchedule',
      'recruitStartsAt',
      'recruitEndsAt',
      'selectionStartsAt',
      'selectionEndsAt',
      'productDeployAt',
      'postingStartsAt',
      'postingEndsAt',
      'isSingleDayPosting',
      'deliveryCompanyName',
      ...fieldNames,
    ] as GetParsedCampaignParams['fieldNames'];

    return getParsedCampaign({ fieldNames: names });
  }, []);

  return (
    <CampaignFormStepOneContext.Provider value={{ isFormValid, getStepOneCampaignFormValues }}>
      {children}
    </CampaignFormStepOneContext.Provider>
  );
};
